import Vue from "vue";
import { PRICETYPES } from "../../../../utilities/constants";


/**
 * Defines the template for the separator between product out of stock and the alternative products
 */
const unavailableSeparatorTemplate = Vue.component("product-unavailable-separator", {
    template: `
        <div class="ct-alternative-unavailable-product-separator"></div>
    `
});

/**
 * Defines the template for the separator for alternative products in stock
 */
const productSeparator = Vue.component("product-separator", {
    template: `
        <div class="ct-alternative-product-separator"></div>
    `
});

/**
 * Defines the template for product badge
 */
const badgeTemplate = Vue.component("product-badge", {
    template: `
        <div :class="['ct-product-tile-badge ' + brandID + '-cart-tile-badge ct-product-tile-badge--promotional ' + brandID + '-product-tile-badge--promotional', badge.isIvaZero ? 'ct-product-tile-badge--iva-zero' : '' ]" v-if="badge && badge.img">
            <img class="img-fluid lazyload hidden"
                loading="lazy"
                :data-src="badge.img"
                :alt="badge.alt"
                :title="badge.title"
            />

            <div :class="'ct-product-tile-badge-value-wrapper ' + brandID + '-product-tile-badge-value-wrapper' + ' ct-product-tile-badge-value-wrapper--' + badge.type + ' ' + brandID + '-product-tile-badge-value-wrapper--' + badge.type + (badge.type == 'superprice' && badge.showDiscountMessage ? ' ct-product-tile-badge-value-wrapper--with-text ' + brandID + '-product-tile-badge-value-wrapper--with-text' : '') + (badge.floatValue ? ' ct-product-tile-badge-value-wrapper--float ' + brandID + '-product-tile-badge-value-wrapper--float' : '')" v-if="badge.discount && badge.unit && badge.showDiscountValue">
                <div class="ct-product-tile-badge-addition d-flex flex-column align-self-center text-center" v-if="badge.showPlusDESignOnBadge">
                    <span :class="'ct-product-tile-badge-addition--sign ' + brandID + '-product-tile-badge-addition--sign'">
                        {{badge.plusLabel}}
                    </span>
                    <span :class="'ct-product-tile-badge-addition--text ' + brandID + '-product-tile-badge-addition--text'">
                        {{badge.thanLabel}}
                    </span>
                </div>
                <span :class="'ct-product-tile-badge-value--' + badge.type + ' ' + brandID + '-product-tile-badge-value--' + badge.type">
                    {{badge.discount}}
                </span>
                <span :class="'ct-product-tile-badge-value--' + badge.type + '-quantifier ' + brandID + '-product-tile-badge-value--' + badge.type + '-quantifier ' + (badge.type == 'superprice' && badge.showDiscountMessage ? ' d-none' : '')">
                    {{badge.unit}}
                </span>
            </div>
        </div>
    `,
    props: ["badgeInfo", "brandID"],
    computed: {
        badge: function () {
            const badgeInfo = this.badgeInfo;
            return badgeInfo ? (badgeInfo.promo ? badgeInfo.promo : badgeInfo.general) : null;
        }
    }
});

/**
 * Defines the template for product advertisement badge
 */
const badgeAdvertisementTemplate = Vue.component("product-badge-advertisement", {
    template: `
        <div :class="'ct-product-tile-badge ' + brandID + '-cart-tile-badge ct-product-tile-badge--advertised ' + brandID + '-product-tile-badge--advertised'"  v-if="badge && badge.img">
            <img class="img-fluid lazyload hidden"
                loading="lazy"
                :data-src="badge.img"
                :alt="badge.alt"
                :title="badge.title"
            />
        </div>
    `,
    props: ["badgeInfo", "brandID"],
    computed: {
        badge: function () {
            const badgeInfo = this.badgeInfo;
            return badgeInfo.advertised;
        }
    }
});


/**
 * Defines the template for product image
 */
const productImageTemplate = Vue.component("product-image", {
    template: `<div class="ct-image-container col-4 col-sm" data-confirmation-image="" >
        <a :href="item.productURL" class="position-relative">
            <picture class="intrinsic intrinsic--square" v-if="item.productTileImage">
                <img class="ct-tile-image lazyload hidden"
                    loading="lazy"
                    @error="fallBackImageURL"
                    :data-src="item.productTileImage.url"
                    :alt="item.productTileImage.alt"
                    :title="item.productTileImage.title"
                >
            </picture>
            <product-badge-advertisement :badgeInfo="item.badgeInfo" :brandID="item.productBrandID" v-if="item.badgeInfo" />
        </a>
    </div>`,
    methods: {
        fallBackImageURL: function (e) {
            e.target.src = this.resources.noImage;
        }
    },
    props: ["item", "resources"],
    components: {
        badgeAdvertisementTemplate
    }
});

/**
 * Defines the template for product details
 */
const productDetailsTemplate = Vue.component("product-details", {
    template: `<div class="ct-pdp-details">
        <div class="ct-pdp-link">
            <a class="pwc-tile--description"
                :href="item.productURL">
                {{ item.productName }}
            </a>
        </div>
        <p class="pwc-tile--brand">{{ item.brand }}</p>
        <p class="pwc-tile--quantity">{{ item.measurementNote }}</p>
    </div>`,
    props: ["item"]
});

/**
 * Defines the template for product price
 */
const productPriceTemplate = Vue.component("product-price", {
    template: `
        <div class="pwc-price-wrap col-12">
            <div class="price">
                <div class="prices-wrapper">
                    <span class="sales pwc-tile--price-primary" v-if="salePrice && salePrice.salePriceValue">
                        <span class="value" :content="salePrice.salePriceValue">
                            <span class="ct-price-formatted">
                                {{ salePrice.salePriceFormatted }}
                            </span>
                            <span class="pwc-m-unit" v-if="pricePerUnit && pricePerUnit.primaryMeasurementUnit">
                                {{ pricePerUnit.primaryMeasurementUnit }}
                            </span>
                        </span>
                    </span>
                    <span class="strike-through list pwc-tile--price-dashed" v-if="listPrice && listPrice.listPriceValue && !badgePVPR.isPVPR">
                        <span class="value pwc-tile--price-value" :content="listPrice.listPriceValue">
                            {{ listPrice.listPriceFormatted }}
                        </span>
                        <span class="pwc-m-unit" v-if="pricePerUnit && pricePerUnit.primaryMeasurementUnit">
                            {{ pricePerUnit.primaryMeasurementUnit }}
                        </span>
                    </span>
                </div>
                <div class="pwc-tile--price-secondary" v-if="pricePerUnit && pricePerUnit.secondaryPrice">
                    <span class="ct-price-value">
                        {{ pricePerUnit.secondaryPrice.price }}
                    </span>
                    <span class="pwc-m-unit" v-if="pricePerUnit && pricePerUnit.secondaryMeasurementUnit">
                        {{ pricePerUnit.secondaryMeasurementUnit }}
                    </span>
                </div>
                <div class="strike-through list pwc-tile--price-dashed d-none" v-if="listPrice && listPrice.listPriceValue && !badgePVPR.isPVPR">
                    <span class="value pwc-tile--price-value" :content="listPrice.listPriceValue">
                        {{ listPrice.listPriceFormatted }}
                    </span>
                    <span class="pwc-m-unit" v-if="pricePerUnit && pricePerUnit.primaryMeasurementUnit">
                        {{ pricePerUnit.primaryMeasurementUnit }}
                    </span>
                </div>
                <p v-bind:class="badgePVPR.badgeClass" v-if="badgeInfo && badgeInfo.promo && badgeInfo.promo.msg">{{ badgeInfo.promo.msg }}</p>
            </div>
        </div>
    `,
    props: ["item"],
    computed: {
        salePrice: function () {
            const {price} = this.item;
            const salePrice = price ? price.sales : null;

            return {
                salePriceFormatted: salePrice ? salePrice.formatted : null,
                salePriceValue: salePrice ? salePrice.value : null
            };
        },
        listPrice: function () {
            const {price} = this.item;
            const listPrice = price ? price.list : null;

            return {
                listPriceFormatted: listPrice ? listPrice.formatted : null,
                listPriceValue: listPrice ? listPrice.value : null
            };
        },
        pricePerUnit: function () {
            const {pricePerUnit} = this.item;
            const secondaryPrice = pricePerUnit && pricePerUnit.secondaryPrice ? pricePerUnit.secondaryPrice : null;
            const primaryPrice = pricePerUnit && pricePerUnit.primaryPrice ? pricePerUnit.primaryPrice : null;
            const isPrimaryPricePerUnit = primaryPrice && primaryPrice.unit;
            const isSecondaryPricePerUnit = secondaryPrice && secondaryPrice.unit;
            const primaryMeasurementUnit = isPrimaryPricePerUnit ? "/" + primaryPrice.unit.toLowerCase() : "";
            const secondaryMeasurementUnit = isSecondaryPricePerUnit ? "/" + secondaryPrice.unit.toLowerCase() : "";

            return {
                primaryMeasurementUnit: primaryMeasurementUnit,
                secondaryMeasurementUnit: secondaryMeasurementUnit,
                secondaryPrice: secondaryPrice
            };
        },
        badgeInfo: function () {
            const {badgeInfo} = this.item;
            return badgeInfo;
        },
        badgePVPR: function () {
            let badgeClass = "pwc-discount-amount";
            let isPVPR = false;
            const {badgeInfo} = this.item;

            if (badgeInfo && badgeInfo.promo && badgeInfo.promo.type && badgeInfo.promo.type == PRICETYPES.PVPR) {
                badgeClass = badgeClass + " pwc-discount-amount-pvpr";
                isPVPR = true;
            }

            return {
                badgeClass: badgeClass,
                isPVPR: isPVPR
            };
        }
    }
});

/**
 * Defines the template for add to cart button
 */
const productAddToCartButtonTemplate = Vue.component("product-add-to-cart-button", {
    template: `
        <button type="button" :class="['button button--primary dynamic-layout-btn ct-alternative-product-replace', resources && resources.fromList ? 'js-list-alternative-product-replace' : 'js-alternative-product-replace']"
            :data-orig-pid="origID"
            :data-product-id="item.id"
            :data-pid="item.id"
            :data-quantity="resources.productQuantity"
            :data-confirmation-image='JSON.stringify(confirmationImage)'
            :data-delay-time="confirmationDelayTime"
            :data-product-name="productName"
            :data-in-cart-msg="resources.inCartLabel"
            :data-one-product-added="resources.inCartOneProduct"
            :data-remove-from-cart-msg="resources.removeFromCartLabel"
            :data-stay-open="resources.confirmationStayOpen"
            :data-url="resources.replaceProductURL"
        >
            <span class="d-block">
                <span class="d-flex" v-html="cartIcon" />
            </span>
        </button>
    `,
    props: ["cartIcon", "item", "resources", "origID"],
    computed: {
        confirmationImage: function () {
            return this.item.confirmationImage;
        },
        confirmationDelayTime: function () {
            return this.item.confirmationDelayTime;
        },
        productName: function () {
            return this.item.productName;
        }
    }
});

export { badgeTemplate, unavailableSeparatorTemplate, productSeparator, productPriceTemplate, productAddToCartButtonTemplate, productImageTemplate, productDetailsTemplate };
